import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import config from 'utils/config';

import RadioFieldSet from 'components/ui/RadioFieldSet';
import { StyledAssistedPassengerSelection, RadioButtonLegend, RadioButtonInfo } from './AssistedPassengerSelection.styles';
import { disabilityTypes } from '../../BookingMagnet.constants';
import WheelchairPassengerSelection from './WheelchairPassengerSelection';
import NonWheelchairPassengerSelection from './NonWheelchairPassengerSelection';
import CompanionOnlyPassengerSelection from './CompanionOnlyPassengerSelection';
import EvacuationChairPassengerSelection from './EvacuationChairPassengerSelection';

const disabilityTypeComponents = {
  wheelchair: WheelchairPassengerSelection,
  nonWheelchair: NonWheelchairPassengerSelection,
  companionOnly: CompanionOnlyPassengerSelection,
  evac: EvacuationChairPassengerSelection
};

const AssistedPassengerSelection = ({
  disabilityType,
  onChangeDisabilityType,
  onChangePassengers,
  isEurostarAgent,
  shouldShowDogOnlyAssistance,
  isCallCenterAgent,
  containSNCBDBOrNSStation,
  containDBStation
}) => {
  const IS_EVACUATION_CHAIR_ENABLED = config('ENABLE_EVACUATION_CHAIR', { canOverride: true }) === 'true';
  const PassengerTypeComponent = disabilityTypeComponents[disabilityType];
  const showQuestions = disabilityType && ![disabilityTypes.ASSISTANCE_DOG_ONLY].includes(disabilityType);

  const additionalButtons = [];

  if (isEurostarAgent) {
    additionalButtons.push(
      {
        value: disabilityTypes.NON_WHEELCHAIR,
        label: <FormattedMessage id="wheelchairSearch.answers.nonWheelchair" />
      },
      {
        value: disabilityTypes.COMPANION_ONLY,
        label: <FormattedMessage id="wheelchairSearch.answers.companionOnly" />
      }
    );
    if (shouldShowDogOnlyAssistance) {
      additionalButtons.push({
        value: disabilityTypes.ASSISTANCE_DOG_ONLY,
        label: <FormattedMessage id="wheelchairSearch.answers.assistanceDogOnly" />
      });
    }
    if (isCallCenterAgent && IS_EVACUATION_CHAIR_ENABLED) {
      additionalButtons.push({
        value: disabilityTypes.EVAC,
        label: <FormattedMessage id="wheelchairSearch.answers.evac" />
      });
    }
  }

  return (
    <StyledAssistedPassengerSelection>
      <>
        <RadioFieldSet
          noDefault
          name="disabilityType"
          legend={
            <>
              <RadioButtonInfo>
                <FormattedMessage id="wheelchairSearch.info.disabilityType" />
              </RadioButtonInfo>
              <RadioButtonLegend>
                <FormattedMessage id="wheelchairSearch.questions.disabilityType" />
              </RadioButtonLegend>
            </>
          }
          radioButtons={[
            {
              value: disabilityTypes.WHEELCHAIR,
              label: <FormattedMessage id="wheelchairSearch.answers.wheelchair" />
            },
            ...additionalButtons
          ]}
          selectedValue={disabilityType}
          onChange={({ target: { value } }) => onChangeDisabilityType(value)}
        />
        {showQuestions && (
          <PassengerTypeComponent
            onChangePassengers={onChangePassengers}
            containSNCBDBOrNSStation={containSNCBDBOrNSStation}
            containDBStation={containDBStation}
            disabilityType={disabilityType}
          />
        )}
      </>
    </StyledAssistedPassengerSelection>
  );
};

AssistedPassengerSelection.propTypes = {
  onChangePassengers: PropTypes.func.isRequired,
  onChangeDisabilityType: PropTypes.func.isRequired,
  disabilityType: PropTypes.oneOf(Object.values(disabilityTypes)),
  isEurostarAgent: PropTypes.bool,
  shouldShowDogOnlyAssistance: PropTypes.bool,
  isCallCenterAgent: PropTypes.bool,
  containSNCBDBOrNSStation: PropTypes.bool,
  containDBStation: PropTypes.bool
};

export default AssistedPassengerSelection;
